import { useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';
import { PrimaryButton, SecondaryButton } from './Button';

export type ModalSize = 'sm' | 'md' | 'lg';

interface ModalAction {
  label: string;
  onClick: () => void;
  busy?: boolean;
  disabled?: boolean;
}

interface StandardModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  size?: ModalSize;
  steps?: { total: number; current: number };
  primaryAction?: ModalAction;
  secondaryAction?: ModalAction;
  destructive?: boolean;
  children: ReactNode;
  contentClassName?: string;
  closeOnOverlayClick?: boolean;
}

const sizeClasses: Record<ModalSize, string> = {
  sm: 'w-full max-w-[480px]',
  md: 'w-full max-w-[520px]',
  lg: 'w-full max-w-[560px]',
};

/**
 * Data-surface modal as specified by Jira OOM-1259.
 *
 * Differs from the dark-theme `Modal.tsx`:
 *  - Light surface, modal-title orange (`--bui-modal-title`).
 *  - Footer hosts primary/secondary actions via props.
 *  - Optional progress indicator for multi-step flows.
 *  - Focus restored to the trigger after close.
 *
 * Use this modal inside CRUD flows. Keep `Modal.tsx` for in-chrome dialogs
 * that need to match the dark dashboard.
 */
export function StandardModal({
  open,
  title,
  onClose,
  size = 'md',
  steps,
  primaryAction,
  secondaryAction,
  destructive = false,
  children,
  contentClassName,
  closeOnOverlayClick = true,
}: StandardModalProps): JSX.Element | null {
  const titleId = useId();
  const lastFocused = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    lastFocused.current = (document.activeElement as HTMLElement) ?? null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !primaryAction?.busy) {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);

    queueMicrotask(() => {
      const focusable = containerRef.current?.querySelector<HTMLElement>(
        '[autofocus], [data-autofocus], button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    });

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      lastFocused.current?.focus?.();
    };
  }, [open, onClose, primaryAction?.busy]);

  if (!open) return null;

  const handleOverlay = (): void => {
    if (closeOnOverlayClick && !primaryAction?.busy) onClose();
  };

  const stepsClamped = steps
    ? {
        total: Math.max(1, steps.total),
        current: Math.min(Math.max(1, steps.current), Math.max(1, steps.total)),
      }
    : null;

  return (
    <div
      className="bui-scope fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <button
        type="button"
        aria-label="Close modal"
        onClick={handleOverlay}
        className="absolute inset-0 cursor-default"
        style={{ backgroundColor: 'var(--bui-overlay)' }}
      />
      <div
        ref={containerRef}
        className={clsx(
          'relative flex max-h-[85vh] flex-col overflow-hidden',
          sizeClasses[size],
        )}
        style={{
          backgroundColor: 'var(--bui-surface)',
          boxShadow: 'var(--bui-shadow-modal)',
          borderRadius: 'var(--bui-radius-modal)',
        }}
      >
        <header
          className="flex items-start justify-between gap-4 px-5 py-4"
          style={{ borderBottom: '1px solid var(--bui-divider)' }}
        >
          <h2
            id={titleId}
            className="text-lg font-semibold leading-tight"
            style={{ color: 'var(--bui-modal-title)' }}
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            disabled={primaryAction?.busy}
            className="rounded-md px-2 py-1 text-sm transition-colors hover:bg-[var(--bui-surface-alt)] disabled:opacity-50"
            style={{ color: 'var(--bui-text-secondary)' }}
          >
            ✕
          </button>
        </header>

        {stepsClamped && <StepBar total={stepsClamped.total} current={stepsClamped.current} />}

        <div className={clsx('flex-1 overflow-y-auto px-5 py-4', contentClassName)}>{children}</div>

        {(primaryAction || secondaryAction) && (
          <footer
            className="flex items-center justify-end gap-2 px-5 py-3"
            style={{
              backgroundColor: 'var(--bui-footer-muted)',
              borderTop: '1px solid var(--bui-divider)',
            }}
          >
            {secondaryAction && (
              <SecondaryButton
                onClick={secondaryAction.onClick}
                disabled={secondaryAction.disabled || primaryAction?.busy}
              >
                {secondaryAction.label}
              </SecondaryButton>
            )}
            {primaryAction && (
              <PrimaryButton
                onClick={primaryAction.onClick}
                busy={primaryAction.busy}
                disabled={primaryAction.disabled}
                style={
                  destructive
                    ? {
                        backgroundColor: 'var(--bui-danger)',
                      }
                    : undefined
                }
              >
                {primaryAction.label}
              </PrimaryButton>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}

function StepBar({ total, current }: { total: number; current: number }): JSX.Element {
  return (
    <div
      className="h-1 w-full"
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={total}
      aria-valuenow={current}
      aria-label={`Step ${current} of ${total}`}
      style={{ backgroundColor: 'var(--bui-divider)' }}
    >
      <div
        className="h-full transition-all"
        style={{
          width: `${(current / total) * 100}%`,
          backgroundColor: 'var(--bui-accent)',
        }}
      />
    </div>
  );
}
